import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  InputAdornment,
  IconButton,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../api/api";
import AuthLayout from "../components/AuthLayout";
import PasswordStrengthPanel from "../components/PasswordStrengthPanel";
import { evaluatePasswordStrength, PASSWORD_MAX_LENGTH } from "../utils/passwordPolicy";
import { confirmPasswordInputProps } from "../utils/passwordFieldHandlers";

const extrairMensagemErro = (err, fallback) => {
  const detalhes = err?.response?.data?.details;
  if (Array.isArray(detalhes) && detalhes.length > 0) {
    return detalhes[0]?.mensagem || fallback;
  }
  return err?.response?.data?.mensagem || fallback;
};

const ResetPassword = () => {
  const theme = useTheme();
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tokenStatus, setTokenStatus] = useState("loading");
  const [tokenMessage, setTokenMessage] = useState("");

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  }, []);

  const passwordEvaluation = useMemo(
    () => evaluatePasswordStrength(password),
    [password]
  );

  const canSubmit =
    tokenStatus === "valid" &&
    passwordEvaluation.isValid &&
    password === passwordConfirm &&
    !loading;

  useEffect(() => {
    let ativo = true;

    const validarToken = async () => {
      if (!token) {
        setTokenStatus("invalid");
        setTokenMessage("Link inválido ou ausente.");
        return;
      }

      try {
        await api.get("/auth/reset-password/verify", { params: { token } });
        if (!ativo) return;
        setTokenStatus("valid");
        setTokenMessage("");
      } catch (err) {
        if (!ativo) return;
        setTokenStatus("invalid");
        setTokenMessage(
          extrairMensagemErro(err, "Link inválido, expirado ou já utilizado.")
        );
      }
    };

    validarToken();
    return () => {
      ativo = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!token) {
      setError("Token inválido ou ausente.");
      setLoading(false);
      return;
    }

    if (!passwordEvaluation.isValid) {
      setError("A senha não atende aos requisitos de segurança.");
      setLoading(false);
      return;
    }

    if (password !== passwordConfirm) {
      setError("As senhas não conferem.");
      setLoading(false);
      return;
    }

    try {
      await api.post("/auth/reset-password", {
        token,
        password,
        password_confirm: passwordConfirm,
      });
      setSuccess("Senha definida com sucesso. Redirecionando para o login...");
      setTokenStatus("used");
      setTimeout(() => {
        window.location.href = "/login";
      }, 1800);
    } catch (err) {
      setError(extrairMensagemErro(err, "Não foi possível definir a senha."));
    } finally {
      setLoading(false);
    }
  };

  const linkIndisponivel = tokenStatus === "invalid" || tokenStatus === "used";

  return (
    <AuthLayout
      title="Definir senha de acesso"
      subtitle="Crie uma senha segura para acessar o painel administrativo"
    >
      {tokenStatus === "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {linkIndisponivel && tokenStatus !== "loading" && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {tokenMessage ||
            "Este link não está mais disponível. Solicite um novo link ou faça login se já definiu sua senha."}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {tokenStatus === "valid" && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <TextField
            label="Nova senha"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            autoComplete="new-password"
            inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <PasswordStrengthPanel
            password={password}
            confirmPassword={passwordConfirm}
          />

          <TextField
            label="Confirmar nova senha"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            fullWidth
            required
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            inputProps={confirmPasswordInputProps}
            error={Boolean(passwordConfirm) && password !== passwordConfirm}
            helperText={
              passwordConfirm && password !== passwordConfirm
                ? "As senhas não conferem."
                : "Digite a senha novamente (colar não permitido)."
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!canSubmit}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              borderRadius: 2,
              textTransform: "none",
              bgcolor: theme.palette.primary.main,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={26} sx={{ color: "#fff" }} />
            ) : (
              "Salvar senha"
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Este link expira em 60 minutos e só pode ser usado uma vez.
          </Typography>
        </Box>
      )}

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mt: 2 }}
      >
        <Typography
          component="span"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => (window.location.href = "/login")}
        >
          Voltar para login
        </Typography>
      </Typography>
    </AuthLayout>
  );
};

export default ResetPassword;
